import { ComparableDetailSection } from "./ComparableDetailSection";
import { ComparableDetailCard } from "./ComparableDetailCard";
import { Icon } from "react-migration/components/Icon";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { ZooplaComparable } from "react-migration/lib/typings/Zoopla";
import { IComparablesTransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { useMemo } from "react";
import { ZooplaAccessLevel } from "react-migration/domains/comparables/util/getZooplaAccessLevel";
import { EpcTag } from "react-migration/layouts/map/Multilayer/layer_types/UKPropertyInformationLayerType/UKPropertyInformationSelectionSidebar/UKPropertyInformationSelectionSidebar";
import { EpcEnergyRating } from "react-migration/layouts/map/Multilayer/layer_types/UKPropertyInformationLayerType/UKPropertyInformationSelectionSidebar/useProperties";
import { MeasurementSystem } from "src/js/stores/user/store";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import { ftsqToMsq, msqToFtsq } from "src/js/util/units_and_constants";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { openAddOnsStoreModal } from "src/js/stores/navigation/actions";
import { HOMETRACK_ADDON_ROUTE } from "react-migration/domains/nav/components/Modals/AddOnsStoreModal/constants";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import clsx from "clsx";
import { Button } from "react-migration/components/Button";
import { epcSearchURL } from "react-migration/domains/comparables/util/Links";

export type Attribute = "bedrooms" | "bathrooms" | "receptions" | "yearBuilt" | "floorArea" | "epcRating";

export type AttributeType = {
  title: string;
  type: Attribute;
  value: string | React.ReactNode;
  disabled?: boolean;
};

type AttributeCardProps = Omit<AttributeType, "value"> & {
  value: string | React.ReactNode | Record<"rating" | "efficiency", string | number | null>;
  subValue?: string | React.ReactNode;
  onClick?: () => void;
};

export const ComparablePropertyAttributes = ({
  record,
  zooplaData,
  loading,
  accessLevel,
}: {
  record?: IComparablesTransactionDTO;
  zooplaData?: ZooplaComparable;
  loading?: boolean;
  accessLevel: ZooplaAccessLevel;
}) => {
  const { t } = useTranslation();
  const unitPreference = getMeasurementSystemPreference();
  const isUSUser = hasFeature(Feature.usAccess);

  const bedrooms = useMemo(() => {
    if (zooplaData?.attributes?.bedrooms) {
      return zooplaData.attributes.bedrooms.toString();
    } else if (record?.est_bedrooms && record?.est_bedrooms.length > 0) {
      return `${record?.est_bedrooms[0]}${record?.est_bedrooms.length > 1 ? `-${record?.est_bedrooms[1]}` : ""}${
        isUSUser ? "*" : ""
      }`;
    } else if (record?.num_bedrooms) {
      return record?.num_bedrooms.toString();
    } else {
      return "N/A";
    }
  }, [record, zooplaData]);

  const epcRating = useMemo(() => {
    if (zooplaData?.energy?.currentEnergyRating) {
      return {
        rating: zooplaData?.energy?.currentEnergyRating,
        efficiency: zooplaData?.energy?.currentEnergyEfficiency,
      };
    } else if (record?.epc?.current_energy_rating) {
      return {
        rating: record?.epc?.current_energy_rating,
        efficiency: record?.epc?.current_energy_efficiency,
      };
    } else {
      return "N/A";
    }
  }, [record, zooplaData]);

  const getValue = (type: Attribute): Record<"rating" | "efficiency", string | number | null> | string => {
    const getValueOrDefault = (val?: string | number | null) => {
      return val ? val.toString() : "N/A";
    };

    switch (type) {
      case "bedrooms":
        return bedrooms;

      case "bathrooms":
        return getValueOrDefault(zooplaData?.attributes?.bathrooms);

      case "receptions":
        return getValueOrDefault(zooplaData?.attributes?.receptions);

      case "yearBuilt":
        return isUSUser ? getValueOrDefault(record?.year_built) : getValueOrDefault(zooplaData?.attributes?.yearBuilt);

      case "floorArea": {
        const floorAreaDataInMsq = zooplaData?.attributes?.floorArea
          ? ftsqToMsq(zooplaData.attributes.floorArea)
          : record?.total_floor_area
          ? record?.total_floor_area
          : null;

        return floorAreaDataInMsq
          ? unitPreference === MeasurementSystem.METRIC
            ? floorAreaDataInMsq.toFixed(0)
            : msqToFtsq(floorAreaDataInMsq).toFixed(0)
          : "N/A";
      }
      case "epcRating":
        return epcRating;

      default:
        return "N/A";
    }
  };

  const attributes: AttributeCardProps[] = useMemo(() => {
    const defaultAttributeTypes: Attribute[] = ["bedrooms", "floorArea", "epcRating"];
    const lightAccessAttributeTypes: Attribute[] = ["bedrooms", "bathrooms", "receptions", "floorArea", "epcRating"];
    const fullAccessAttributeTypes: Attribute[] = [
      "bedrooms",
      "bathrooms",
      "receptions",
      "yearBuilt",
      "floorArea",
      "epcRating",
    ];
    const USAttributeTypes: Attribute[] = ["bedrooms", "floorArea", "yearBuilt"];

    const UKattributeTypes =
      accessLevel === "full"
        ? fullAccessAttributeTypes
        : accessLevel === "light"
        ? lightAccessAttributeTypes
        : defaultAttributeTypes;

    if (isUSUser) {
      return USAttributeTypes.map((attribute: Attribute) => {
        const value = getValue(attribute);
        return {
          title: t(`comparables.selection_details.attributes.${attribute}`),
          type: attribute,
          value: value,
        };
      });
    }

    return fullAccessAttributeTypes.map((attribute: Attribute) => {
      const value = getValue(attribute);
      const encodedPostcode = encodeURIComponent(record?.post_code || "").replace(/%20/g, "+");
      return {
        title: t(`comparables.selection_details.attributes.${attribute}`),
        type: attribute,
        value:
          attribute === "epcRating" && typeof value === "object" ? (
            <>
              <EpcTag rating={value.rating as EpcEnergyRating} efficiency={value.efficiency as number} size="sm" />
              {encodedPostcode && (
                <Button
                  variant="link"
                  leadIcon="lt-external-link-line"
                  className="no-underline!"
                  onClick={() =>
                    window.open(
                      epcSearchURL({
                        post_code: record?.post_code || "",
                        designation: record?.designation || "Residential",
                      }),
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                />
              )}
            </>
          ) : (
            value
          ),
        disabled: !UKattributeTypes.includes(attribute),
        onClick: !UKattributeTypes.includes(attribute)
          ? () => {
              openAddOnsStoreModal({
                path: HOMETRACK_ADDON_ROUTE,
                logClickSource: "zoopla-preview",
              });
            }
          : undefined,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, zooplaData, accessLevel, unitPreference]);

  if (loading) return <SkeletonPropertyAttributes />;

  return (
    <ComparableDetailSection title="Property attributes">
      <div className={clsx("grid w-full gap-2", isUSUser ? "grid-cols-2" : "grid-cols-3")}>
        {attributes.map((attribute) => (
          <AttributeCard
            key={attribute.type}
            {...attribute}
            subValue={
              attribute.type === "floorArea" &&
              t(unitPreference === MeasurementSystem.METRIC ? "comparables.sqm.alt" : "comparables.sqft.alt")
            }
          />
        ))}
      </div>
    </ComparableDetailSection>
  );
};

function AttributeCard({ title, type, value, subValue, disabled = false, onClick }: AttributeCardProps) {
  const icon = {
    bedrooms: "icon-lt-hotel-bed-line",
    bathrooms: "icon-lt-hotel-bath-line",
    receptions: "icon-lt-sofa-line",
    yearBuilt: "icon-lt-calendar-event-line",
    floorArea: "icon-lt-ruler-2-line",
    epcRating: "icon-lt-epc",
  }[type];

  return (
    <ComparableDetailCard disabled={disabled} size="sm" title={title} onClick={onClick}>
      <div className="flex flex-row items-center gap-1 text-sm font-medium text-blue-grey-950">
        <Icon icon={icon} size="sm" />
        {typeof value === "string" ? value : (value as React.ReactNode)}
        {subValue && <div className="text-xs font-normal text-blue-grey-600a">{subValue}</div>}
      </div>
    </ComparableDetailCard>
  );
}

const SkeletonPropertyAttributes = () => {
  return (
    <ComparableDetailSection title="Property attributes">
      <div className="relative flex flex-row gap-2 w-full h-[54px]">
        <SkeletonLoading rows={1} cols={3} showPadding={false} />
      </div>
      <div className="relative flex flex-row gap-2 w-full h-[54px]">
        <SkeletonLoading rows={1} cols={3} showPadding={false} />
      </div>
    </ComparableDetailSection>
  );
};
